import { allStatuses, Status, StatusFor, useAuth, useGqlClient } from '@aposphaere/core-kit'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { EMPTY_STATUS_ID } from '../../constants'
import { isStatusCancelled } from '../../utils/statusUtils'
import { QueryOptions } from './utils'

export interface StatusWithCancelledProp extends Status {
  isCancelled: boolean
}

interface ParsedStatuses {
  forAppointments: StatusWithCancelledProp[]
  forProjects: StatusWithCancelledProp[]
}

export const useStatusesQuery = (options?: QueryOptions<ParsedStatuses>) => {
  const gqlClient = useGqlClient()
  const auth = useAuth()
  return useQuery(
    'statuses',
    async () => {
      const response = await gqlClient.request<{ statuses: Status[] }>(allStatuses)
      const statuses = response?.statuses
      if (!statuses) {
        return { forAppointments: [], forProjects: [] }
      }
      const appointmentStauses = statuses
        ?.filter((status) => status.for === StatusFor.Appointments)
        .map((status) => ({ ...status, isCancelled: isStatusCancelled(status) }))
      const projectStatuses = statuses
        ?.filter((status) => status.for === StatusFor.Projects)
        .map((status) => ({ ...status, isCancelled: isStatusCancelled(status) }))

      return { forAppointments: appointmentStauses, forProjects: projectStatuses }
    },
    { enabled: !!auth.user, staleTime: Infinity, ...options },
  )
}

export const useCancelledStatusesIds = () => {
  const cancelledStatuses = ['Abgesagt', 'Absage<12Std']
  const statusesQuery = useStatusesQuery()

  return (
    statusesQuery?.data?.forAppointments?.filter((status) => cancelledStatuses.includes(status.label_short)).map((status) => status.id.toString()) ??
    []
  )
}

export const useCompletedStatusId = () => {
  const completedStatus = 'Schulung'
  const statusesQuery = useStatusesQuery()
  return statusesQuery?.data?.forAppointments?.filter((status) => completedStatus === status.label_short).map((status) => status.id.toString()) ?? []
}

export const useStatusesForProject = (selectedStatusId: number | null) => {
  const { data: statuses } = useStatusesQuery()

  const statusesForProjects = statuses?.forProjects

  const statusesForProject = useMemo(() => {
    const EMPTY_STATUS = {
      label: 'Grund für kein Interesse wählen ...',
      id: EMPTY_STATUS_ID,
      is_active: false,
    }

    if (!statusesForProjects?.length) {
      return [{ id: 0, label: 'Laden...', is_active: false }]
    }

    const mapped = statusesForProjects.map(({ id, label, is_active }) => ({
      id,
      label,
      is_active,
    }))

    const active = mapped.filter((s) => s.is_active)

    const selected = mapped.find((s) => +s.id === selectedStatusId)

    const finalStatuses = selected && !selected.is_active ? [...active, selected] : active

    return [EMPTY_STATUS, ...finalStatuses]
  }, [statusesForProjects, selectedStatusId])

  return statusesForProject
}
